import { useState, useMemo, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Search,
  Plus,
  Server,
  Gamepad2,
  Eye,
  Settings,
  RefreshCcw,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { GameService, type GameProvider as APIProvider } from '@/domain/game'

// Extended Provider interface for UI
interface Provider extends APIProvider {
  // UI-specific fields (calculated or default)
  totalBets?: number
  totalPlayers?: number
  revenue?: number
  commission?: number
  categories?: string[]
}

export function ProvidersPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [providers, setProviders] = useState<Provider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Load providers from API
  const loadProviders = async () => {
    try {
      setError(null)
      const result = await GameService.findAllProviders({ query: '' })
      
      // Map API data to UI format with defaults
      const mappedProviders: Provider[] = result.items.map((p) => ({
        ...p,
        // Add UI-specific defaults
        totalBets: 0,
        totalPlayers: 0,
        revenue: 0,
        commission: 0,
        categories: p.providerType ? [p.providerType] : [],
      }))
      
      setProviders(mappedProviders)
    } catch (err: any) {
      console.error('[ProvidersPage] Error loading providers:', err)
      setError(err.message || 'ไม่สามารถโหลดข้อมูลผู้ให้บริการได้')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }

  // Initial load
  useEffect(() => {
    loadProviders()
  }, [])

  // Refresh handler
  const handleRefresh = () => {
    setIsRefreshing(true)
    loadProviders()
  }

  // Filter providers by search
  const filteredProviders = useMemo(() => {
    if (!searchTerm) return providers
    const term = searchTerm.toLowerCase()
    return providers.filter(p =>
      p.name.toLowerCase().includes(term) ||
      p.code.toLowerCase().includes(term)
    )
  }, [searchTerm, providers])

  // Stats
  const stats = useMemo(() => ({
    total: providers.length,
    online: providers.filter(p => p.isActive !== false && p.status === 'ACTIVE').length,
    totalGames: providers.reduce((sum, p) => sum + (p.gamesCount || 0), 0),
    totalRevenue: providers.reduce((sum, p) => sum + (p.revenue || 0), 0),
  }), [providers])

  const formatNumber = (num: number) => new Intl.NumberFormat('th-TH').format(num)

  // Format last sync time
  const formatLastSync = (dateStr?: string) => {
    if (!dateStr) return '-'
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    
    if (diffMins < 1) return 'เมื่อสักครู่'
    if (diffMins < 60) return `${diffMins} นาทีที่แล้ว`
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} ชั่วโมงที่แล้ว`
    return `${Math.floor(diffMins / 1440)} วันที่แล้ว`
  }

  const getStatusBadge = (provider: Provider) => {
    const isOnline = provider.isActive !== false && provider.status === 'ACTIVE'
    const config = isOnline
      ? { label: 'ออนไลน์', icon: <CheckCircle className="h-3 w-3" />, className: 'bg-win/10 text-win' }
      : { label: 'ออฟไลน์', icon: <XCircle className="h-3 w-3" />, className: 'bg-muted text-muted-foreground' }
    return <Badge variant="secondary" className={cn("border-0 gap-1", config.className)}>{config.icon} {config.label}</Badge>
  }

  const getApiStatusBadge = (provider: Provider) => {
    const status = provider.apiStatus || (provider.status === 'ACTIVE' ? 'connected' : 'disconnected')
    const config: Record<string, { label: string; className: string }> = {
      connected: { label: 'เชื่อมต่อ', className: 'bg-win/10 text-win' },
      online: { label: 'เชื่อมต่อ', className: 'bg-win/10 text-win' },
      disconnected: { label: 'ตัดการเชื่อมต่อ', className: 'bg-muted text-muted-foreground' },
      offline: { label: 'ตัดการเชื่อมต่อ', className: 'bg-muted text-muted-foreground' },
      error: { label: 'Error', className: 'bg-destructive/10 text-destructive' },
      maintenance: { label: 'ปรับปรุง', className: 'bg-warning/10 text-warning' },
    }
    const { label, className } = config[status] || config.disconnected
    return <Badge variant="secondary" className={cn("border-0 text-xs", className)}>{label}</Badge>
  }

  // Toggle provider status
  const handleToggleStatus = async (provider: Provider) => {
    try {
      const newStatus = provider.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
      await GameService.toggleProviderStatus(provider.id, newStatus)
      // Reload data
      loadProviders()
    } catch (err: any) {
      console.error('[ProvidersPage] Error toggling status:', err)
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">กำลังโหลดข้อมูลผู้ให้บริการ...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
          <p className="mt-2 text-lg font-medium">เกิดข้อผิดพลาด</p>
          <p className="text-muted-foreground">{error}</p>
          <Button onClick={handleRefresh} className="mt-4">
            <RefreshCcw className="h-4 w-4 mr-2" />
            ลองใหม่
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">ผู้ให้บริการเกม</h1>
          <p className="text-muted-foreground">จัดการผู้ให้บริการเกม (Game Providers)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRefresh} disabled={isRefreshing}>
            <RefreshCcw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
            รีเฟรช
          </Button>
          <Button className="gap-2" disabled>
            <Plus className="h-4 w-4" />
            เพิ่มผู้ให้บริการ
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-primary/10">
                <Server className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ผู้ให้บริการทั้งหมด</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-win/10">
                <CheckCircle className="h-5 w-5 text-win" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">ออนไลน์</p>
                <p className="text-xl font-bold text-win">{stats.online}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-blue-500/10">
                <Gamepad2 className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">เกมทั้งหมด</p>
                <p className="text-xl font-bold">{formatNumber(stats.totalGames)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500/10">
                <Clock className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">สถานะ</p>
                <p className="text-xl font-bold text-win">เชื่อมต่อ API</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="ค้นหาผู้ให้บริการ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* No data */}
      {filteredProviders.length === 0 && (
        <div className="text-center py-12">
          <Server className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="mt-2 text-lg font-medium">ไม่พบผู้ให้บริการ</p>
          <p className="text-muted-foreground">
            {searchTerm ? 'ลองค้นหาด้วยคำอื่น' : 'กรุณา Sync ข้อมูลเกมก่อน'}
          </p>
        </div>
      )}

      {/* Provider Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredProviders.map((provider) => (
          <Card key={provider.id} className={cn("overflow-hidden", !provider.isActive && "opacity-60")}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden">
                    {provider.logo ? (
                      <img 
                        src={provider.logo} 
                        alt={provider.name}
                        className="h-full w-full object-contain"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none'
                        }}
                      />
                    ) : (
                      <Server className="h-6 w-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <CardTitle className="text-base">{provider.name}</CardTitle>
                    <p className="text-xs text-muted-foreground">{provider.code}</p>
                  </div>
                </div>
                <Switch 
                  checked={provider.isActive !== false} 
                  onCheckedChange={() => handleToggleStatus(provider)}
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                {getStatusBadge(provider)}
                {getApiStatusBadge(provider)}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-2 rounded-lg bg-muted/30">
                  <p className="text-lg font-bold">{provider.gamesCount || 0}</p>
                  <p className="text-xs text-muted-foreground">เกม</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-muted/30">
                  <p className="text-lg font-bold">{provider.providerType || '-'}</p>
                  <p className="text-xs text-muted-foreground">ประเภท</p>
                </div>
              </div>

              {provider.categories && provider.categories.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {provider.categories.map((cat) => (
                    <Badge key={cat} variant="outline" className="text-xs">{cat}</Badge>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t text-xs text-muted-foreground">
                <span>Sync: {formatLastSync(provider.lastSyncAt)}</span>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSelectedProvider(provider)}>
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled>
                    <Settings className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Provider Detail Dialog */}
      <Dialog open={!!selectedProvider} onOpenChange={() => setSelectedProvider(null)}>
        <DialogContent className="max-w-lg">
          {selectedProvider && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center overflow-hidden">
                    {selectedProvider.logo ? (
                      <img 
                        src={selectedProvider.logo} 
                        alt={selectedProvider.name}
                        className="h-full w-full object-contain"
                      />
                    ) : (
                      <Server className="h-6 w-6 text-primary" />
                    )}
                  </div>
                  <div>
                    <p>{selectedProvider.name}</p>
                    <p className="text-sm font-normal text-muted-foreground">{selectedProvider.code}</p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-2">
                  {getStatusBadge(selectedProvider)}
                  {getApiStatusBadge(selectedProvider)}
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{selectedProvider.gamesCount || 0}</p>
                    <p className="text-xs text-muted-foreground">เกมทั้งหมด</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{selectedProvider.providerType || '-'}</p>
                    <p className="text-xs text-muted-foreground">ประเภท</p>
                  </div>
                  <div className="text-center p-3 rounded-lg bg-muted/30">
                    <p className="text-2xl font-bold">{selectedProvider.isActive ? 'ON' : 'OFF'}</p>
                    <p className="text-xs text-muted-foreground">สถานะ</p>
                  </div>
                </div>

                <div className="space-y-3 pt-4 border-t">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Provider Code</span>
                    <code className="text-xs bg-muted px-2 py-0.5 rounded">{selectedProvider.code}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Provider Type</span>
                    <span>{selectedProvider.providerType || '-'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sync ล่าสุด</span>
                    <span>{formatLastSync(selectedProvider.lastSyncAt)}</span>
                  </div>
                </div>

                {selectedProvider.categories && selectedProvider.categories.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground mb-2">หมวดหมู่เกม</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedProvider.categories.map((cat) => (
                        <Badge key={cat} variant="secondary">{cat}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  <Button variant="outline" className="flex-1 gap-2" disabled>
                    <RefreshCcw className="h-4 w-4" /> Sync เกม
                  </Button>
                  <Button className="flex-1 gap-2" disabled>
                    <Settings className="h-4 w-4" /> ตั้งค่า
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
